import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { switchMap, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { firstValueFrom, Subscription, Subject, BehaviorSubject, combineLatest } from 'rxjs';
import {
  CompanyService,
  CostItem,
  CostItemService,
  EnumService,
  FileArrays,
  ItemGroup,
  ItemGroupService,
  LoadingService,
  LocalStorageService,
  ModalService,
  NameIdPair,
  PaidByListItem,
  Project,
  ProjectManagerService,
  StubCostItem,
  DataSyncService,
} from '@ops/shared-types';
import { TranslateService } from '@ngx-translate/core';
import { OpsCustomCurrencyModalComponent, CustomCurrencyModalConfig } from '@ops/storybook';

import { Miscellanious } from '@ops/global-config';

@Component({
  selector: 'ops-add-cost',
  templateUrl: './add-cost.component.html',
  styleUrls: ['./add-cost.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCostComponent implements OnInit, OnDestroy, OnChanges {
  //TODO: when we switch to multiple instances of the same department, the list of department options should be:
  //      project departments "type + title/name" cancatenated with
  //      departments that are not in the projecct "'create' + department.name"
  //      when the user selects a non-existing dept, a new item group is added to the project
  @Input() id: string;
  @Input() parentId: string;
  isLoading: boolean = false;
  @Input('projectId') set setProjectId(projectId: string) {
    this.projectId = projectId;
    this.paymentMethodOptions = null;
    this.loadData();
  }
  @Input() department?: { name: string; _id: string };

  @Output() costAdded: EventEmitter<CostItem> = new EventEmitter<CostItem>();
  showAddNewOption: boolean = false;
  // Use BehaviorSubject for reactive state management
  private readonly destroy$ = new Subject<void>();
  private readonly validationSubject$ = new BehaviorSubject<number>(0);
  
  item: CostItem = { ...StubCostItem };

  // Computed properties with caching
  private _isValid = false;
  private _taxAmount = 0;
  private _costIncTax = 0;
  private _costIncTaxExchange = 0;
  private _taxAmountExchange = 0;
  private _hasDifferentCurrency = false;
  private _currencySymbol = '';
  private _currencyExchangeSymbol = '';

  get isValid(): boolean {
    return this._isValid;
  }

  get taxAmount(): number {
    return this._taxAmount;
  }

  get costIncTax(): number {
    return this._costIncTax;
  }

  get costIncTaxExchange(): number {
    return this._costIncTaxExchange;
  }

  get taxAmountExchange(): number {
    return this._taxAmountExchange;
  }

  get hasDifferentCurrency(): boolean {
    return this._hasDifferentCurrency;
  }

  get currencySymbol(): string {
    return this._currencySymbol;
  }

  get currencyExchangeSymbol(): string {
    return this._currencyExchangeSymbol;
  }

  isOpen: boolean;

  projectId: string;
  companyId: string;

  projectList: Array<{ name: string; _id: string; versionLink: string }>;
  projectOptions: Array<string>;
  selectedProject: { name: string; _id: string; versionLink: string };

  projectDepartments: Array<{ name: string; _id: string; isTeam: boolean }>;
  departmentList: Array<{ name: string; _id: string; isTeam: boolean }>;
  departmentOptions: Array<string>;
  selectedDepartment: { name: string; _id?: string; isTeam?: boolean };

  positionList: Array<{ name: string; _id: string; department: string }>;
  selectedPosition: string;

  dateOfBillString: string;

  positionOptions: Array<string>;
  costStatusOptions: Array<string>;
  costTypeOptions: Array<string>;
  paymentMethodOptions: Array<string>;
  paidByOptions: Array<string>;
  assignToOptions: Array<string>;
  categoryOptions: Array<string>;

  currencyOptions: Array<string>;

  // Custom currency properties
  showCustomCurrencyForm: boolean = false;
  customCurrencyConfig: CustomCurrencyModalConfig = {
    excludeCurrencyCodes: [],
    excludeReason: 'This currency is already in use',
    onSuccess: {
      updateField: 'item.currencyCode',
      additionalData: { refreshOptions: true }
    }
  };

  assignToValues: Array<NameIdPair>;

  cost: string;

  itemCounter: number = 1;

  isDepartmentFixed: boolean;

  companyCurrencyCode?: string;

  searchPosition: string;
  allPositions: Array<string>;

  hasTax: boolean = false;

  private element: HTMLElement | null;

  baseCurrencyCode: string;

  files: FileArrays = {
    invoiceFiles: [],
    paymentFiles: [],
  };

  paidByNameIdList: Array<PaidByListItem>;

  // Cache for enum options to avoid repeated API calls
  private enumOptionsCache = new Map<string, any[]>();
  private assignToCache = new Map<string, NameIdPair[]>();
  private lastCompanyId: string = '';

  constructor(
    private pm: ProjectManagerService,
    private modalService: ModalService,
    private enumService: EnumService,
    private itemGroupService: ItemGroupService,
    private costItemService: CostItemService,
    private companyService: CompanyService,
    private el: ElementRef,
    private loadingService: LoadingService,
    private localStorageService: LocalStorageService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private dataSyncService: DataSyncService
  ) {
    this.element = this.el.nativeElement;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && changes['projectId'].currentValue) {
      this.loadData();
    }
  }

  async ngOnInit(): Promise<void> { 
    // console.log('add-cost-modal-dashboard', this.id);
    this.modalService.add(this);
    // console.log('add-cost-modal-dashboard', this.id);

    await this.loadEnumOptions();

    if (!this.paymentMethodOptions) {
      await this.getAllPaidByOptions();
    }

    if (this.element) {
      this.element.focus();
    }

    if (!this.id) {
      console.error('@Input id is mandatory');
      return;
    }

    // if (this.element) document.body.appendChild(this.element);
    

    // Load enum options once and cache them (these are static and don't depend on projectId)
    //
    
    // Set up validation with debouncing
    this.setupValidation();

    // Load payment method options after a short delay to ensure proper initialization
    // setTimeout(async () => {
    //   await this.getAllPaidByOptions();
    // }, 1000);

    // If projectId is available, load project-specific data
    if (this.projectId) {
      this.loadData();
    }
  }

  private async loadEnumOptions(): Promise<void> {
    const companyId = this.localStorageService.getCurrentCompany()?._id || this.companyId || '';
    
    try {
      // Load synchronous enum options
      const costStatusOptions = this.getCachedEnumOptions('costStatus', () => this.enumService.getCostStatuseOptions());
      const costTypeOptions = this.getCachedEnumOptions('costType', () => this.enumService.getCostTypeOptions());
      const categoryOptions = this.getCachedEnumOptions('category', () => this.enumService.getAccountingCategoryOptions());
      const currencyOptions = this.getCachedEnumOptions('currency', () => this.enumService.getCurrencyOptions());
      
      // Load async enum options with caching
      const assignToValues = await this.getCachedAssignToOptions(companyId);

      this.costStatusOptions = costStatusOptions;
      this.costTypeOptions = costTypeOptions;
      this.assignToValues = assignToValues;
      this.assignToOptions = assignToValues.map((a) => a.name);
      this.categoryOptions = categoryOptions;
      this.currencyOptions = [
        '+ Add New', // Add custom currency option
        ...currencyOptions
      ];
      
      // Trigger change detection since we're using OnPush strategy
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error loading enum options:', error);
      this.cdr.markForCheck();
    }
  }

  private getCachedEnumOptions(key: string, loader: () => any[]): any[] {
    if (this.enumOptionsCache.has(key)) {
      return this.enumOptionsCache.get(key)!;
    }
    
    const options = loader();
    this.enumOptionsCache.set(key, options);
    return options;
  }

  private async getCachedAssignToOptions(companyId: string): Promise<NameIdPair[]> {
    // Check if we have cached data for this company ID
    if (this.assignToCache.has(companyId)) {
      return this.assignToCache.get(companyId)!;
    }
    
    // Check if company ID has changed
    if (this.lastCompanyId && this.lastCompanyId !== companyId) {
      this.assignToCache.clear();
    }
    
    const assignToValues = await this.enumService.getAssignToOptions(companyId);
    
    // Cache the result
    this.assignToCache.set(companyId, assignToValues);
    this.lastCompanyId = companyId;
    
    return assignToValues;
  }

  private async updateAssignToOptions(companyId: string): Promise<void> {
    const assignToValues = await this.getCachedAssignToOptions(companyId);
    this.assignToValues = assignToValues;
    this.assignToOptions = assignToValues.map((a) => a.name);
    this.cdr.markForCheck();
  }

  private setupValidation(): void {
    // Debounced validation to reduce change detection cycles
    this.validationSubject$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateComputedValues();
        this.cdr.markForCheck();
      });
  }

  private updateComputedValues(): void {
    // Update all computed properties at once
    this._taxAmount = this.calculateTaxAmount();
    this._costIncTax = this.calculateCostIncTax();
    this._costIncTaxExchange = this.calculateCostIncTaxExchange();
    this._taxAmountExchange = this.calculateTaxAmountExchange();
    this._hasDifferentCurrency = this.calculateHasDifferentCurrency();
    this._currencySymbol = this.calculateCurrencySymbol();
    this._currencyExchangeSymbol = this.calculateCurrencyExchangeSymbol();
    this._isValid = this.validate();
  }

  private calculateTaxAmount(): number {
    if (!this.item || !this.item.cost || !this.item.taxPercentage) return 0;
    return this.item.cost * (this.item.taxPercentage / 100);
  }

  private calculateCostIncTax(): number {
    if (!this.hasTax) return this.item.cost || 0;
    if (!this.hasTax || !this.item || !this.item.cost || !this.item.taxPercentage) return 0;
    return this.item.cost * (1 + this.item.taxPercentage / 100);
  }

  private calculateCostIncTaxExchange(): number {
    return this.costIncTax * (this.item.exchangeRate || 0);
  }

  private calculateTaxAmountExchange(): number {
    return this.taxAmount * (this.item.exchangeRate || 0);
  }

  private calculateHasDifferentCurrency(): boolean {
    if (!this.item.currencyCode) return false;
    this.companyCurrencyCode = this.localStorageService.getCurrentCompany()?.baseCurrencyCode;
    if (this.item.currencyCode && this.companyCurrencyCode) {
      return this.item.currencyCode !== this.companyCurrencyCode;
    }
    return false;
  }

  private calculateCurrencySymbol(): string {
    if (!this.item.currencyCode || this.item.currencyCode === '+ Add New') {
      return '';
    }
    return Miscellanious.currencySymbolFromCode('en-US', this.item.currencyCode);
  }

  private calculateCurrencyExchangeSymbol(): string {
    return Miscellanious.currencySymbolFromCode('en-US', this.companyCurrencyCode);
  }

  async getAllPaidByOptions() {
    const company = this.localStorageService.getCurrentCompany()?._id || this.companyId || '';
    
    try {
      // ✅ CHANGED: Use all payment method options (unfiltered) to show all methods
      // This allows users to see all payment methods even if no accounts/cards are configured
      this.paymentMethodOptions = this.enumService.getAllPaymentMethodOptions();
      
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error loading payment method options:', error);
      this.cdr.markForCheck();
    }
  }

  //use subject and subscriptino for this
  async loadData() {
    if (!this.projectId || this.isLoading) {
      return;
    }
    
    this.selectedProject = null; 
    
    this.isLoading = true;
    
    const start = new Date().getTime();
    // console.log('START LOAD DATA', start);

    const company = this.localStorageService.getCurrentCompany()?._id || this.companyId || '';
   
    // Optimize data loading with parallel requests and better error handling
    const sub = this.pm
      .getOrCreateProjectCost(this.projectId, company)
      .pipe(
        switchMap((projectCostId: string) => {
          return combineLatest([
            this.pm.getProjectById(projectCostId),
            this.itemGroupService.getAllProjectItemGroups(projectCostId),
            this.enumService.getAllDepartments(this.projectId),
            this.itemGroupService.getItemGroupPositions(this.projectId, 'all')
          ]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: ([project, groups, depts, positions]) => {
          this.selectedProject = {
            _id: project._id,
            name: project.name ?? '',
            versionLink: project.versionLink ?? '',
          };
          const newCompanyId = project.company ?? this.localStorageService.getCurrentCompany()?._id ?? '';
          
          // Check if company ID has changed and update assignTo options if needed
          if (this.companyId !== newCompanyId) {
            this.companyId = newCompanyId;
            this.updateAssignToOptions(newCompanyId);
          }
          
          this.element?.click();
          this.item.project = project._id;
          
          this.projectDepartments = groups.map((g) => ({
            _id: g._id,
            name: g.department,
            isTeam: g.isTeam ?? false,
          }));
          
          this.departmentList = depts;
          this.departmentOptions = depts.map((d) => d.name);
          
          this.positionList = positions.map((p) => ({
            _id: p._id,
            name: p.name,
            department: p.department
          }));
          
          this.allPositions = positions.map((p) => p.name);
          this.baseCurrencyCode = this.localStorageService.getCurrentCompany()?.baseCurrencyCode || '';
          
          this.cdr.markForCheck();
          this.isLoading = false;
          // console.log('END LOAD DATA', new Date().getTime() - start);
        },
        error: (error) => {
          console.error('Error loading data:', error);
          this.cdr.markForCheck();
          this.isLoading = false;
        },
        complete: async () => {
          if (!this.paymentMethodOptions) {
            await this.getAllPaidByOptions();
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.modalService.remove(this.id);
    this.element?.remove();
    
    this.selectedProject = null;
    // Clear caches to prevent memory leaks
    this.enumOptionsCache.clear();
    this.assignToCache.clear();
  }

  openModal(id: string) {
    // console.log('openModal', id);
    this.modalService.open(id);
  }
  
  closeModal(id: string) { 
    this.modalService.close(id);
  }
  
  open(department: { _id: string; name: string }): void {
    this.item = { ...StubCostItem };
    this.dateOfBillString = '';
    this.department = department;

    if (this.department) {
      this.onSelectDepartment(this.department.name);
    }

    // if (this.element) this.element.style.display = 'flex';
    this.isOpen = true;
    this.isDepartmentFixed = !!this.department;
    this.hasTax = false;

    if (this.element) this.element?.focus();

    delete this.item.currencyCode;
    delete this.item.exchangeRate;
    
    this.cdr.markForCheck();
  }

  close(event?: any): void {
    event?.stopPropagation();
    // this.selectedProject = null;
    // this.projectId = null;
    this.item = { ...StubCostItem };
    this.selectedDepartment = null;
    this._isValid = false;
    this.itemCounter = 1;
    this.files = {
      invoiceFiles: [],
      paymentFiles: [],
    };
    // if (this.element) this.element.style.display = 'none';
    this.isOpen = false;
    this.searchPosition = '';
    
    this.cdr.markForCheck();
  }

  onCounterUp() {
    this.itemCounter++;
    this.triggerValidation();
  }

  onCounterDown() {
    if (this.itemCounter > 1) {
      this.itemCounter--;
    }
    this.triggerValidation();
  }

  onSelectDepartment(selection: string) {
    const projectDepartment = this.departmentList.find((p) => p.name === selection);

    if (projectDepartment) {
      this.selectedDepartment = projectDepartment;
    }
    if (!this.selectedDepartment) {
      this.selectedDepartment = this.departmentList.find((d) => d.name === selection) ?? { name: '', _id: '', isTeam: false };
      this.selectedDepartment._id = 'NEW';
    }
    this.item.itemGroup = this.selectedDepartment._id;
    if (this.department?._id && this.department?._id != 'NEW') {
      this.item.itemGroup = this.department._id;
    }
    this.item.position = undefined;
    this.searchPosition = '';
    this.selectedPosition = '';
    this.positionOptions = this.positionList
      .filter((p) => p.department === selection)
      .map((p) => p.name);
    this.triggerValidation();
  }

  onTypeSearch(event: any) {
    this.searchPosition = event.target.value;
    if (this.searchPosition.length > 1) {
      this.positionOptions = this.allPositions.filter((p) =>
        p?.toUpperCase().includes(this.searchPosition?.toUpperCase())
      );
      this.positionOptions.unshift(this.translateService.instant('+ ADD NEW'));
      return;
    }
    if (this.saveDescription.length < 2) {
      this.positionOptions = this.positionList
        .filter((p) => p.department === this.selectedDepartment.name)
        .map((p) => p.name);
    }
  }

  onCustomPosition() {
    this.item.position = this.searchPosition;
    this.triggerValidation();
  }

  onSelectPosition(selection: string) {
    if (selection === this.translateService.instant('+ ADD NEW')) {
      this.item.position = this.searchPosition;
      this.triggerValidation();
      return;
    }
    this.item.position = selection;
    this.searchPosition = selection;
    this.triggerValidation();
  }

  onSelectCostStatus(selection: string) {
    this.item.costStatus = selection;
    this.triggerValidation();
  }
  
  onSelectCostType(selection: string) {
    this.item.costType = selection;
    this.triggerValidation();
  }

  onSelectpaidBy(selection: string) {
    if (selection === 'Add New') {
      if (this.item.paymentMethod === 'Bank Transfer') {
        this.onAddNewBankAccount();
      } else if (this.item.paymentMethod === 'Credit Card') {
        this.onAddNewCreditCard();
      }
      return;
    }
    this.item.paidBy = selection;
    if (!this.item.currencyCode) {
      this.item.currencyCode = this.paidByNameIdList.find((p) => p.name === selection)?.currencyCode;
    }
    this.triggerValidation();
  }
  
  onSelectPaymentMethod(selection: string) {
    this.showAddNewOption = selection === 'Bank Transfer' || selection === 'Credit Card';
    this.item.paymentMethod = selection;
    this.item.paidBy = undefined;
    this.setPaidByOptions(selection);
    this.triggerValidation();
  }
  
  onSelectBillDate(selection: Date) {
    this.item.dateOfBill = selection;
    this.triggerValidation();
  }
  
  onSelectPaymentDate(selection: Date) {
    this.item.paymentDate = selection;
    this.triggerValidation();
  }
  
  onSelectAssignTo(selection: string) {
    this.item.assignedTo = selection;
    this.triggerValidation();
  }
  
  onSelectCategory(selection: string) {
    this.item.accountingCategory = selection;
    this.triggerValidation();
  }

  onInputDescription(event: any) {
    if (event.key === 'Enter') {
      this.item.description = event.target.value.slice(0, -1);
      event.srcElement.blur();
    }
  }
  
  onInputName(event: any) {
    if (event.key === 'Enter') {
      this.item.name = event.target.value.slice(0, -1);
      event.srcElement.blur();
    }
  }

  onSelectCurrency(selection: string) {
    if (selection === '+ Add New') {
      this.toggleCustomCurrencyForm();
      return;
    }
    
    this.item.currencyCode = selection;
    if (this.baseCurrencyCode === this.item.currencyCode) {
      setTimeout(() => {
        this.item.exchangeRate = 1;
        this.triggerValidation();
      }, 50);
      return;
    }
    this.item.exchangeRate = 0;
    this.triggerValidation();
  }

  onEditExchangeRate(value: number) {
    this.item.exchangeRate = value ?? 1;
    this.triggerValidation();
  }

  calculateReciprocal(): void {
    if (this.item?.exchangeRate && this.item.exchangeRate !== 0) {
      this.item.exchangeRate = 1 / this.item.exchangeRate;
      this.onEditExchangeRate(this.item.exchangeRate);
    }
  }

  onInputCost(event: any) {
    this.item.cost = +event.target.value.toString().replaceAll(',', '');
    if (event.key === 'Enter') {
      this.item.cost = +event.target.value.slice(0, -1).toString().replaceAll(',', '');
      event.srcElement.blur();
    }
    this.triggerValidation();
  }

  saveDescription(event: { target: { value: string | undefined } }) {
    this.item.description = event.target.value;
  }
  
  saveName(event: { target: { value: string | undefined } }) {
    this.item.name = event.target.value;
  }

  onToggleTax() {
    this.item.taxPercentage = 10;
    if (this.hasTax) {
      this.item.taxPercentage = 0;
    }
    this.hasTax = !this.hasTax;
    this.triggerValidation();
  }

  onToggleReminder() {
    this.item.hasReminder = !this.item.hasReminder;
  }

  async onSave() {
    if (
      !(this.files?.invoiceFiles?.length > 0 || this.files?.paymentFiles?.length > 0) &&
      this.item.exchangeRate === 0
    ) {
      window.alert(this.translateService.instant('windowAlert.addCost.exchangeRateCannotBeZero'));
      return;
    }
    if (!this.isValid) return;
    this.searchPosition = '';

    const projectDepartment = this.projectDepartments.find(
      (p) => p.name === this.selectedDepartment.name
    );

    if (this.item.itemGroup === 'NEW' || !projectDepartment) {
      const department = await firstValueFrom(
        this.itemGroupService.createItemGroup(
          this.selectedProject._id,
          this.selectedDepartment.isTeam ?? false,
          this.selectedDepartment.name
        )
      );
      this.selectedDepartment._id = department._id;
      this.item.itemGroup = department._id;
      this.saveItems();
      return;
    }

    this.saveItems();
  }

  private saveItems() {
    this.loadingService.setForceLoading(true);

    const projectDepartment = this.projectDepartments.find(
      (p) => p.name === this.selectedDepartment.name
    );

    const itemGroupId = projectDepartment?._id || this.item?.itemGroup;
    this.item.itemGroup = undefined;
    this.item.project = undefined;
    this.item.days = 1;
    this.item.units = 1;
    this.item.cost = this.item.cost ?? 0;
    this.item.paymentMethod = this.item.paymentMethod ? this.item.paymentMethod : null;
    this.item.costStatus = this.item.costStatus ? this.item.costStatus : null;

    // Fix Blink Paid By
    const itemPaidBy = this.paidByNameIdList?.find((p) => p.name === this.item.paidBy)?.id ?? null;

    let assignedTo: string;

    if (this.item.assignedTo) {
      assignedTo = this.assignToValues.find((a) => a.name === this.item.assignedTo).id;
    }
    delete this.item.accountingCategory;

    if (this.itemCounter === 1) {
      const sub = this.costItemService
        .createCostItemByGroup(
          itemGroupId ?? '',
          { ...this.item, assignedTo: assignedTo, paidBy: itemPaidBy },
          this.selectedProject._id,
          this.companyId
        )
        .pipe(
          switchMap((item: CostItem) => {
            return this.costItemService.attachFiles(item, 'invoice', this.files.invoiceFiles);
          }),
          switchMap((item: CostItem) => {
            return this.costItemService.attachFiles(item, 'payment', this.files.paymentFiles);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: (item: CostItem) => {
            this.loadingService.setForceLoading(false);
            this.costAdded.emit(item);
            this.close();
            return;
          },
          error: (error) => {
            this.loadingService.setForceLoading(false);
            window.alert(`${error.error.message}`);
            return;
          },
        });
      return;
    }
    
    //TODO: forkjoin
    for (let index = 0; index < this.itemCounter; index++) {
      const sub = this.costItemService
        .createCostItemByGroup(
          itemGroupId ?? '',
          { ...this.item, assignedTo: assignedTo, paidBy: itemPaidBy },
          this.selectedProject._id,
          this.companyId
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe((c: CostItem) => {
          this.costAdded.emit(c);
        });
      
      if (index === this.itemCounter - 1) {
        this.close();
      }
    }
  }

  async setPaidByOptions(paymentMethod: string, callback?: Function) {
    const data = await this.enumService.getPaidByOptions(paymentMethod, this.companyId);
    this.paidByNameIdList = data.paidByList;
    this.paidByOptions = data.optionsList;
    callback && callback();
    this.triggerValidation();
  }

  attachFiles(event: any, type: 'invoice' | 'payment') {
    var files: Array<File> = event.target.files as Array<File>;
    files = [...(files as Array<File>)];

    files.forEach((f, index, array) => {
      if (f.size > 1024 * 1024 * 15) {
        window.alert(`${f.name} ${this.translateService.instant('windowAlert.addCost.fileTooBig')}`);
        array.splice(index, 1);
        event.target.value = '';
        return;
      }
      if(this.files.invoiceFiles?.find((d) => (d.name === f.name) && (type === 'invoice'))
      || this.files.paymentFiles?.find((d) => (d.name === f.name) && (type === 'payment'))) {
        window.alert(this.translateService.instant('windowAlert.documentModal.fileAlreadyUploaded', { fileName: f.name }));
        array.splice(index, 1);
        event.target.value = '';
        return;
      }
    });

    switch (type) {
      case 'invoice':
        {
          if (files.length + this.files.invoiceFiles.length > 3) {
            window.alert(this.translateService.instant('windowAlert.addCost.cannotAttachMoreThanThreeInvoiceFiles'));
            return;
          }
          this.files.invoiceFiles.push(...files);
        }
        break;
      case 'payment':
        {
          if (files.length + this.files.paymentFiles.length > 3) {
            window.alert(this.translateService.instant('windowAlert.addCost.cannotAttachMoreThanThreePaymentConfirmationFiles'));
            return;
          }
          this.files.paymentFiles.push(...files);
        }
        break;
      default:
        break;
    }
    event.target.value = '';
    this.triggerValidation();
  }

  onPhotoInvoice(event: Event): void {
    if (this.files.invoiceFiles.length > 3) {
      window.alert(this.translateService.instant('windowAlert.addCost.cannotAttachMoreThanThreeInvoiceFiles'));
      return;
    }

    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.files.invoiceFiles.push(file);
      this.triggerValidation();
    }
  }

  onPhotoPayment(event: Event): void {
    if (this.files.paymentFiles.length > 3) {
      window.alert(this.translateService.instant('windowAlert.addCost.cannotAttachMoreThanThreeInvoiceFiles'));
      return;
    }

    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.files.paymentFiles.push(file);
      this.triggerValidation();
    }
  }

  removeFile(file: File, type: 'invoice' | 'payment', index: number) {
    switch (type) {
      case 'invoice':
        this.files.invoiceFiles.splice(index, 1);
        break;
      case 'payment':
        this.files.paymentFiles.splice(index, 1);
        break;
      default:
        break;
    }
    window.alert(`${file.name} ${this.translateService.instant('windowAlert.addCost.fileRemoved')}`);
    this.triggerValidation();
  }

  validate() {
    if (!this.selectedDepartment?._id || !this.item.dateOfBill || !this.item.paymentDate) {
      return false;
    }
    if (this.files?.invoiceFiles?.length > 0 || this.files?.paymentFiles?.length > 0) {
      return true;
    }

    if(this.hasTax && !this.item.taxPercentage) {
      return false;
    }

    if (
      !this.item.position ||
      !this.item.costStatus ||
      !this.item.costType ||
      this.itemCounter <= 0 ||
      !this.item.paymentMethod ||
      !this.item.paidBy ||
      (this.hasTax && !this.item.taxPercentage) ||
      !this.item.exchangeRate ||
      !this.item.cost 
    ) {
      return false;
    }
    return true;
  }

  triggerValidation(): void {
    this.validationSubject$.next(this.validationSubject$.value + 1);
  }

  onEditTax(event: any) {
    this.item.taxPercentage = +event.target.value.replaceAll(',', '');
    this.triggerValidation();
  }


  get addNewOptionType(): 'bank-account' | 'credit-card' | null {
    if (this.item.paymentMethod === 'Bank Transfer') {
      return 'bank-account';
    } else if (this.item.paymentMethod === 'Credit Card') {
      return 'credit-card';
    }
    return null;
  }

  onAddNewBankAccount(): void {
    this.modalService.open('bank-account-create-modal-add-cost');
  }

  onAddNewCreditCard(): void {
    this.modalService.open('credit-card-create-modal-add-cost');
  }

  onBankAccountCreated(): void {
    this.isLoading = true;
    // console.log('BANK ACCOUNT CREATED');
    // Refresh the paid by options to include the newly created bank account
    if (this.item.paymentMethod) {
      // console.log('BANK ACCOUNT CREATED 2');
      // this.getAllPaidByOptions();
      this.setPaidByOptions(this.item.paymentMethod, () => {
        this.isLoading = false;
        
        // Notify other components that a bank account was created
        this.dataSyncService.notifyCreditCardCreated();
      });
    } else {
      this.isLoading = false;
      
      // Notify other components that a bank account was created
      this.dataSyncService.notifyCreditCardCreated();
    }
    // Notify parent component to refresh cashflow items
    // this.paidByOptionsRefreshed.emit();
  }

  onCreditCardCreated(): void {
    this.isLoading = true;
    // console.log('CREDIT CARD CREATED');
    // Refresh the paid by options to include the newly created credit card
    if (this.item.paymentMethod) {
      // console.log('CREDIT CARD CREATED 2');
      // this.getAllPaidByOptions();
      this.setPaidByOptions(this.item.paymentMethod, () => {
        this.isLoading = false;
        
        // Notify other components that a credit card was created
        this.dataSyncService.notifyCreditCardCreated();
      });
    } else {
      this.isLoading = false;
      
      // Notify other components that a credit card was created
      this.dataSyncService.notifyCreditCardCreated();
    }
    // Notify parent component to refresh cashflow items
    // this.paidByOptionsRefreshed.emit();
  }

  // Custom Currency Methods
  toggleCustomCurrencyForm(): void {
    this.showCustomCurrencyForm = !this.showCustomCurrencyForm;
  }

  onCustomCurrencyAdded(event: { 
    currencyCode: string; 
    updateField?: string; 
    additionalData?: any; 
  }): void {
    // Refresh currency options to include the new custom currency
    this.currencyOptions = [
      '+ Add New', // Add custom currency option
      ...this.enumService.getCurrencyOptions()
    ];
    
    // Auto-select the newly added currency
    this.item.currencyCode = event.currencyCode;
    
    // Set exchange rate to 1 if it's the base currency, otherwise reset
    if (this.baseCurrencyCode === this.item.currencyCode) {
      setTimeout(() => {
        this.item.exchangeRate = 1;
        this.triggerValidation();
      }, 50);
    } else {
      this.item.exchangeRate = 0;
      this.triggerValidation();
    }
    
    // Close the modal
    this.showCustomCurrencyForm = false;
  }

  onCustomCurrencyModalClosed(): void {
    this.showCustomCurrencyForm = false;
  }
}
